export default {
  // Page titles
  title: 'Settings',
  team: 'Team',
  section_label: 'Section',

  // Descriptions
  description: 'Manage your account settings',
  team_description: 'Manage team members and roles',

  // Team
  team_section: {
    title: 'Team Members',
    invite: 'Invite Member',
    invite_description: 'Send an invitation to join your team',
    role: 'Role',
    change_role: 'Change Role',
    remove_member: 'Remove Member',
    resend_invite: 'Resend Invite',
    pending: 'Pending',
    login_deleted: 'Login deleted',
    login_deleted_note: 'Deleted by this person. Erased for good after {{date}}. Only they can restore it, by signing in.',
    self_indicator: '(you)',
  },

  // Roles
  roles: {
    owner: 'Owner',
    admin: 'Admin',
    staff: 'Staff',
    accountant: 'Accountant',
  },

  // Invite form
  invite: {
    email: 'Email',
    email_placeholder: 'colleague@example.com',
    first_name: 'First Name',
    last_name: 'Last Name',
    role: 'Role',
    send_invite: 'Send Invitation',
  },

  // Account
  account: {
    title: 'Account Settings',
    name: 'Account Name',
    language: 'Language',
    timezone: 'Timezone',
    date_format: 'Date Format',
    calendar: 'Calendar',
    currency: 'Currency',
    invoice_prefix: 'Invoice Prefix',
    invoice_prefix_placeholder: 'e.g., INV',
    liff_id: 'LINE LIFF ID',
    liff_id_placeholder: 'e.g., 1234567890-xxxxxxxx',
    read_only_notice: 'Only admins and owners can edit account settings.',
    // EP-160 (section 2.7, S1): the data controller named in the tenants' notice.
    privacy_controller: {
      title: 'Personal data controller',
      help: 'Tenants see this name on their My data page. Leave it blank to use the account name and the owner email.',
      name: 'Personal data controller name',
      contact: 'Contact',
    },
  },

  // Languages
  languages: {
    en: 'English',
    th: 'Thai',
  },

  // Payment methods
  payment_methods: {
    title: 'Payment Methods',
    description: 'Configure accepted payment methods for your tenants',
    bank_transfer: {
      title: 'Bank Transfer',
      description: 'Accept payments via bank transfer',
      account_number: 'Account Number',
      account_name: 'Account Name',
      bank_name: 'Bank Name',
    },
    promptpay: {
      title: 'PromptPay',
      description: 'Accept payments via PromptPay QR code',
      stripe_notice: 'Requires Stripe integration',
      publishable_key: 'Stripe Publishable Key',
      secret_key: 'Stripe Secret Key',
      webhook_secret: 'Stripe Webhook Secret',
    },
    credit_card: {
      title: 'Credit Card',
      description: 'Accept credit/debit card payments',
      stripe_notice: 'Requires Stripe integration',
      publishable_key: 'Stripe Publishable Key',
      secret_key: 'Stripe Secret Key',
      webhook_secret: 'Stripe Webhook Secret',
    },
    cash: {
      title: 'Cash',
      description: 'Accept cash payments at the office',
    },
    invoice_qr: {
      title: 'Static QR',
      description: 'Upload a QR code image (e.g., PromptPay) to display on invoices',
      missing: 'The saved QR image can no longer be found. Upload it again.',
      upload_failed: 'The QR image was not uploaded. Try again.',
    },
    coming_soon: 'Coming Soon',
    groups: {
      basic: 'Basic',
      stripe: 'Stripe',
      coming_soon: 'Coming soon',
    },
    paysolution: {
      title: 'PaySolution',
      description: 'Accept payments via PaySolution',
    },
    beam: {
      title: 'Beam',
      description: 'Accept payments via Beam',
    },
    validation: {
      required: 'This field is required when the method is enabled',
    },
    alipay: {
      title: 'Alipay',
      description: 'Accept payments via Alipay',
    },
    wechat_pay: {
      title: 'WeChat Pay',
      description: 'Accept payments via WeChat Pay',
    },
    promptpay_bill_payment: {
      title: 'Dynamic QR',
      description: 'Accept PromptPay Bill Payment QR payments with a bank-issued Biller ID',
      enabled_label: 'Enable Bill Payment QR',
      biller_id_label: 'Biller ID',
      biller_id_helper: 'Bank-issued PromptPay Bill Payment Biller ID',
      ref2_mode_label: 'Reference 2 Mode',
      ref2_mode_description: 'Controls what value is encoded as Reference 2 in the bill payment QR',
      ref2_mode_plate: 'License Plate',
      ref2_mode_none: 'None',
      proxy_target_label: 'Proxy Target (phone or national ID)',
      proxy_target_helper: 'PromptPay proxy number registered with the bank (phone number or 13-digit national ID)',
    },
    // EP-143 T143-010 — Recipient registry: the operator's own receiving accounts.
    // Used by the gate-ladder G4 gate to verify that the tenant paid the right account.
    receiving_accounts: {
      title: 'Receiving Accounts',
      bank_code_label: 'Bank',
      account_number_label: 'Account Number',
      display_name_label: 'Display Name',
      add_button: 'Add Account',
      empty: 'No receiving accounts configured. Add at least one account so the system can verify slip recipients.',
    },
  },

  // Tax Invoice Settings
  tax_invoice: {
    title: 'Tax Invoice Settings',
    description: 'Configure business tax information for generating tax invoices',
    tax_id: 'Tax ID (13 digits)',
    tax_id_placeholder: '0105XXXXXXXXX',
    branch_number: 'Branch Number',
    branch_number_placeholder: 'e.g., 00000 for head office',
    address: 'Tax Address',
    address_placeholder: 'Business address for tax invoices',
    vat_enabled: 'Include VAT',
    vat_rate: 'VAT Rate (%)',
  },

  // EP-111 T111-6 — per-property condominium type module toggle. Single-gate
  // (owner-settable account setting only, no Unleash flag). When on, the
  // property form shows a property-type dropdown and the CAF + Co-owner
  // sidebar sections appear (wired in later tasks).
  property_types: {
    title: 'Property types',
    condominium_enabled_label: 'Condominium properties',
    condominium_enabled_description: 'Lets you mark a property as a condominium and charge a common area fee (CAF). Adds a CAF menu and a Co-owner menu in the sidebar.',
  },

  // EP-136 (T136-026) — account-wide fallback for the booking-flow settings.
  // Any property can override each key individually on its own Booking
  // block; unset keys here fall back to the hardcoded server defaults
  // (deposit mode, forfeit on cancel, no default amount).
  booking: {
    title: 'Booking Settings',
    description: 'Account-wide defaults used when a property does not set its own booking settings.',
    fee_mode_label: 'Booking Fee Treatment',
    fee_default_label: 'Default Booking Amount',
    fee_default_hint: 'Leave blank to require the amount to be entered every time a unit is booked.',
    forfeit_label: 'Forfeit booking fee on cancellation',
    fee_mode_options: {
      deposit: 'Deposit — counts toward the deposit, refundable',
      revenue: 'Revenue — non-refundable reservation fee',
    },
  },


  // Lead Generation Settings
  lead_generation: {
    title: 'Lead Generation',
    description: 'Configure LINE lead generation settings for capturing prospects automatically',
    // EP-156 T156-007: this card shares the tenant OA with the LINE
    // Integration tab, so it shows status + a link there instead of its own
    // secret input.
    line_oa_status_connected: 'Connected · {{name}} ({{basicId}})',
    line_oa_status_not_connected: 'Not connected yet',
    line_oa_shared_hint: 'Uses the same LINE Official Account as LINE Integration',
    go_to_line_settings: 'Go to LINE settings',
  },

  lpr: {
    // EP-53: `enabled` key removed; toggle is now admin-cli only.
    // `title` + `description` retained — referenced by LprEventLogPage:146.
    title: 'License Plate Recognition (LPR)',
    description: 'Enable automatic license plate recognition for parking management',
  },

  // LIFF Settings
  liff: {
    tab_label: 'LINE Integration',
    // EP-156 T156-007 — the tenant OA card (not WIN Pickup's own channel).
    line_oa: {
      title: 'LINE Official Account',
      status_connected: 'Connected · {{name}} ({{basicId}})',
      status_not_connected: 'Not connected yet',
      test_connection: 'Test connection',
      channel_secret: 'Channel Secret',
      channel_secret_placeholder: 'Enter the channel secret',
      channel_secret_configured: 'Configured. Paste a new value to replace it',
      channel_access_token: 'Channel Access Token',
      channel_access_token_placeholder: 'Enter the channel access token',
      channel_access_token_configured: 'Configured. Paste a new value to replace it',
      login_channel_id: 'LINE Login Channel ID',
      login_channel_id_placeholder: 'e.g. 1234567890',
      login_channel_id_hint: 'Fill from the digits before the dash in the LIFF ID',
      auto_checks_heading: 'Checked automatically (Test connection)',
      check_token: 'Token works',
      check_webhook: 'Webhook URL points to FlowRent',
      check_signature: 'Secret matches the webhook',
      manual_checks_heading: 'Done by hand in LINE Developers',
      webhook_url: 'Webhook URL',
      copy: 'Copy',
      copied: 'Copied',
      copy_webhook_url: 'Copy the webhook URL',
      how_to: 'How to',
      manual_step_webhook: 'In the Messaging API channel, turn on Use webhook and paste the Webhook URL below',
      manual_step_liff: 'Create the LIFF app in a LINE Login channel under the same Provider as the OA',
      manual_step_linked_oa: 'In the LINE Login channel, set Linked OA to this OA and turn on the Add friend option',
    },
    liff_id: 'LIFF ID',
    liff_id_placeholder: 'e.g., 1234567890-xxxxxxxx',
    theme_settings: 'Theme Settings',
    login_page_settings: 'Login page',
    primary_color: 'Primary Color',
    button_color: 'Button Color',
    login_bg_color: 'Login Background Color',
    login_bg_image: 'Login Background Image URL',
    login_panel_enabled: 'Show panel behind login form',
    login_panel_color: 'Panel Color',
    login_panel_opacity: 'Panel Opacity',
    login_title_align: 'Title alignment',
    login_title_align_left: 'Left',
    login_title_align_center: 'Center',
    login_title_align_right: 'Right',
    login_panel_margin: 'Panel margin from the screen edge (px, 0 to 48)',
    login_panel_padding: 'Panel padding (px, 0 to 24)',
    login_panel_radius: 'Panel corner radius (px, 0 to 32)',
    login_preview: 'Login Preview',
    // The LIFF lease-code page's own wording, for the preview. Must equal
    // liff/src/i18n/locales/{en,th}/auth.ts (pinned by a test).
    login_preview_page: {
      title: 'Enter your lease code to receive OTP',
      placeholder: 'e.g. AB123456',
      send_otp: 'Submit',
    },
    home_hero_color: 'Home Hero Color',
    home_hero_image: 'Home Hero Image URL',
    font_settings: 'Font',
    font_family: 'Google Font Name',
    font_family_hint: 'Enter a Google Fonts family name (e.g. Bai Jamjuree, Noto Sans Thai, Prompt). Leave empty for default.',
  },

  // EP-157 T157-005 — one account's own white-label app registration (so a
  // tenant device of that app resolves push through the account's OWN
  // Firebase project instead of FlowRent's global one). Owner/admin only;
  // shown to every account, with an empty state for an account on the plain
  // FlowRent app.
  app_integration: {
    tab_label: 'Mobile app',
    title: 'Your mobile app',
    status_connected: 'Connected · last tested {{date}}',
    status_not_connected: 'Not tested yet',
    test_connection: 'Test connection',
    name_label: 'App name',
    ios_bundle_id_label: 'iOS Bundle ID',
    android_package_name_label: 'Android Package Name',
    firebase_project_id_label: 'Firebase Project ID',
    service_account_label: 'Service Account Key',
    service_account_configured: 'Configured ({{email}})',
    service_account_upload_label: 'Upload a .json file',
    service_account_replace_label: 'Upload a new .json file to replace it',
    service_account_new_file_selected: 'New file selected: {{filename}}',
    auto_checks_heading: 'Checked automatically (Test connection)',
    check_key_valid: 'Key works',
    check_fcm_reachable: 'Reaches Firebase Cloud Messaging',
    manual_checks_heading: 'Done by hand in Firebase / Apple',
    save: 'Save',
    save_success: 'App integration saved',
    remove: 'Remove',
    remove_confirm_title: 'Remove this app integration?',
    remove_confirm_message: 'Devices of this app stop receiving notifications until it is set again.',
    remove_confirm_button: 'Remove',
    remove_success: 'App integration removed',
    how_to: 'How to',
    manual_step_apns_key: 'Create an APNs key (.p8) in Apple Developer → Keys',
    manual_step_apns_upload: 'Upload the APNs key in Firebase → Project settings → Cloud Messaging',
    manual_step_service_account: 'Generate a service account key in Firebase → Project settings → Service accounts and upload the .json file above',
    manual_step_push_app_id: 'Turn on Push Notifications for the App ID in Apple Developer → Identifiers',
  },

  // EP-73 C4 — LINE rich menu (owner-only)
  rich_menu: {
    title: 'LINE Rich Menu',
    access_token: 'Channel Access Token',
    token_placeholder: 'Paste the channel access token',
    token_configured: 'Configured. Paste a new token to replace it',
    save_token: 'Save Token',
    token_saved: 'Channel access token saved',
    token_save_error: 'Could not save the token',
    image: 'Rich Menu Image',
    image_hint: 'PNG/JPEG, either 2500×843 (one button) or 2500×1686 (five buttons). Applied as the default menu for all LINE users.',
    apply: 'Apply Rich Menu',
    apply_success: 'Rich menu applied to your LINE Official Account',
    apply_error: 'Could not apply the rich menu. Check the token, LIFF ID, and image',
  },

  // EP-73 — AI feature coach
  ai_coach: {
    title: 'AI Feature Coach',
    description: 'A gentle in-app assistant that suggests the next FlowRent feature to set up, with a quick walkthrough.',
    max_features: 'Max features to suggest',
    max_features_hint: 'How many different features the coach may introduce over time.',
    saved: 'Coach settings saved',
    save_error: 'Could not save coach settings',
  },

  // Parcel Carriers
  parcel_carriers: {
    title: 'Carrier Settings',
    description: 'Manage the list of parcel carriers available for selection',
    carrier_list: 'Carrier List',
    add_carrier: 'Add',
    add_placeholder: 'Enter carrier name...',
    reset_defaults: 'Reset to Defaults',
    remove_carrier: 'Remove carrier',
  },

  // Maintenance Categories
  maintenance_categories: {
    title: 'Categories',
    description: 'Manage maintenance request categories',
    add_placeholder: 'Enter category name...',
    add_placeholder_en: 'English name',
    add_placeholder_th: 'Thai name',
    add_category: 'Add',
    reset_defaults: 'Reset to Defaults',
    remove_category: 'Remove category',
  },

  // Expense Categories
  expense_categories: {
    title: 'Expense Categories',
    description: 'Manage expense categories for property expense tracking',
    add_placeholder_en: 'English name',
    add_placeholder_th: 'Thai name',
    add_category: 'Add',
    reset_defaults: 'Reset to Defaults',
    remove_category: 'Remove category',
    gl_account_for: 'GL account for {{name}}',
    select_gl_account: 'Select expense account',
    loading_gl_accounts: 'Loading expense accounts…',
    unavailable_gl_account: '{{code}} — unavailable account',
    gl_accounts_error: 'Expense accounts could not be loaded. Try again later.',
    no_gl_accounts: 'Create or activate an expense account in Chart of Accounts.',
    deposit_return_gl: '2300 — Security deposits received (fixed mapping)',
  },

  // Customer Sources
  customer_sources: {
    title: 'Customer Sources',
    description: 'Configure how customers found your property. These options appear in the customer form.',
    add_source: 'Add Source',
    key: 'Key',
    name_en: 'Name (EN)',
    name_th: 'Name (TH)',
    reset_defaults: 'Reset to Defaults',
    no_sources: 'No customer sources configured.',
    add_placeholder_en: 'English name',
    add_placeholder_th: 'Thai name',
    remove_source: 'Remove source',
  },

  // Activity Log
  activity_log: {
    title: 'Activity Log',
    description: 'View audit trail and change history',
    type_label: 'Type',
    action_label: 'Action',
    user_label: 'User',
    all_types: 'All Types',
    all_actions: 'All Actions',
    all_users: 'All Users',
    date_from: 'Date From',
    date_to: 'Date To',
    empty_title: 'No activity yet',
    empty_description: 'Changes to your data will appear here.',
    record_deleted: 'Record was deleted',
    // EP-160 S4: a change made by the FlowRent team in a support session.
    support_actor: 'FlowRent team ({{name}}) · session #{{id}}',
    support_actor_no_session: 'FlowRent team ({{name}})',
    removed_value: '[removed]',
    actions: {
      create: 'Create',
      update: 'Update',
      destroy: 'Delete',
    },
    model_types: {
      Property: 'Property',
      Customer: 'Customer',
      Unit: 'Unit',
      Lease: 'Lease',
      Invoice: 'Invoice',
      Payment: 'Payment',
      MaintenanceRequest: 'Maintenance Request',
      Parcel: 'Parcel',
      RoomType: 'Room Type',
      Meter: 'Meter',
      Reading: 'Reading',
      DocumentTemplate: 'Template',
    },
  },


  // Replay Onboarding
  replay_onboarding: {
    button: 'Replay onboarding',
    confirm_title: 'Replay onboarding?',
    confirm_body: 'This will reset your onboarding progress. Your existing properties, customers, leases, etc. will NOT be deleted.',
    confirm_yes: 'Yes, replay',
  },

  // Activity Log link (admin/owner-only affordance on SettingsPage — F-011)
  activity_log_link: {
    title: 'Activity Log',
    subtitle: 'See who changed what',
  },

  // EP-99 WIN pickup relay operator settings (single-gate: win_pickup_enabled)
  win_pickup: {
    tab_label: 'WIN Pickup',
    description: 'Configure the WIN @winbot LINE group relay for package pickup notifications. Enable this module from the admin console first.',

    // Bind code section
    bind_code_title: 'LINE Group Binding',
    bind_code_hint: 'In your LINE group chat, type "@winbot connect <code>" with this code to link the group for pickup notifications.',
    bind_code_label: 'Bind Code',
    copy_bind_code: 'Copy bind code',
    copy: 'Copy',
    copied: 'Copied!',
    rotate_bind_code: 'Rotate Code',
    rotate_bind_code_hint: 'Generating a new code invalidates the current one. The LINE group must re-bind after rotation.',
    bind_code_rotated: 'Bind code rotated successfully',
    bind_code_rotate_error: 'Could not rotate the bind code',

    // Bind status chips
    bind_status: {
      pending_bind: 'Pending',
      active: 'Active',
      inactive: 'Inactive',
    },

    // LINE OA credentials section
    credentials_title: 'LINE OA Credentials',
    credentials_description: 'Enter your LINE Official Account credentials. The access token is write-only and cannot be retrieved once saved.',
    channel_secret: 'Channel Secret',
    channel_secret_placeholder: 'Paste your LINE channel secret',
    channel_secret_configured: 'Configured. Paste a new value to replace it',
    channel_secret_hint: 'Found in LINE Developers console → Messaging API.',
    access_token: 'Channel Access Token',
    access_token_placeholder: 'Paste your LINE channel access token',
    access_token_configured: 'Configured. Paste a new value to replace it',
    access_token_hint: 'Found in LINE Developers console → Messaging API → Channel access token.',
    save_credentials: 'Save Credentials',
    creds_saved: 'LINE credentials saved',
    creds_error: 'Could not save LINE credentials',

    // Fare-board image section
    fare_board_title: 'Fare Board Image',
    fare_board_description: 'Upload an image of the fare board shown to customers in the LINE group (e.g. delivery fee schedule).',
    fare_board_preview_alt: 'Fare board preview',
    fare_board_upload_label: 'Choose fare board image',
    fare_board_uploading: 'Uploading…',
    fare_board_hint: 'Supported formats: PNG, JPEG, WebP.',
    fare_board_saved: 'Fare board image saved',
    fare_board_error: 'Could not upload the fare board image',
  },

  // EP-144: Settings > Subscription tab (owner-only, read-only)
  subscription: {
    tab_label: 'Subscription',
    plan: {
      title: 'Current plan',
    },
    tier_label: 'Plan',
    tiers: {
      standard: 'Standard',
      invitation: 'Invitation',
    },
    statuses: {
      active: 'Active',
      canceled: 'Canceled',
    },
    billing_cycle_label: 'Billing cycle',
    cycles: {
      monthly: 'Monthly',
    },
    started_at_label: 'Started',
    room_count_label: 'Rooms',
    next_invoice: {
      title: 'Next invoice (estimate)',
      period: '{{from}} to {{to}}',
      total: 'Total',
      note: 'The final amount is calculated from the room count on the billing date.',
    },
    items: {
      subscription_fee: '{{tier}} plan · {{rooms}} rooms',
    },
    pricing: {
      title: 'Pricing',
      rooms: 'Rooms',
      monthly: 'Monthly (per room)',
      annual: 'Annual (per room/year)',
      range: '{{min}}-{{max}}',
      range_open: '{{min}}+',
      link: 'See flowrent.io/pricing',
    },
    invoices: {
      title: 'Invoices',
      columns: {
        number: 'Number',
        period: 'Period',
        rooms: 'Rooms',
        total: 'Total',
        due_at: 'Due',
        status: 'Status',
      },
      status: {
        pending: 'Pending',
        paid: 'Paid',
        void: 'Void',
      },
      empty: 'No invoices yet',
      empty_description: 'Your first invoice is issued on the 1st of next month.',
    },
    invoice_detail: {
      items: 'Charges',
      columns: {
        description: 'Description',
        quantity: 'Quantity',
        unit_price: 'Unit price',
        amount: 'Amount',
      },
      subtotal: 'Subtotal',
      details: 'Invoice details',
      period_range: 'Billing period',
      emailed_at: 'Emailed',
      not_emailed: 'Not emailed',
      payment: 'Payment',
      payment_amount: 'Amount paid',
      paid_at: 'Paid on',
      payment_method: 'Method',
      payment_methods: {
        transfer: 'Bank transfer',
        promptpay: 'PromptPay',
        other: 'Other',
      },
      no_payment: 'No payment recorded yet.',
      not_found: 'This invoice could not be found.',
      back: 'Back to subscription',
    },
    none: {
      title: 'No subscription set up yet',
      description: 'Contact the FlowRent team to activate one.',
    },
    errors: {
      load_failed: 'Could not load your subscription.',
    },
  },

  // EP-160 T160-201w: Settings -> FlowRent help (S2). The owner allows the
  // FlowRent team in for a limited time; an admin sees it read-only.
  support_access: {
    tab_label: 'FlowRent help',
    title: 'Help from the FlowRent team',
    intro_1: 'Let the FlowRent team help in your account for a limited time.',
    intro_2: 'Every change is recorded and shown in the history below.',
    intro_3: 'Each time the team opens a tenant’s full details, it is recorded per tenant.',
    intro_4: 'The FlowRent team never sees ID card numbers, slip images, ID card images or bank accounts.',
    admin_read_only: 'Only the account owner can allow or withdraw access.',
    load_failed: 'Could not load the FlowRent help settings.',
    none_active: 'The FlowRent team has no access to this account right now.',
    unknown_staff: 'name not given',
    scopes: {
      read: 'Look only',
      setup: 'Set up for me',
      tenants: 'Set up and enter my tenants',
    },
    scope_notes: {
      read: 'Can look but change nothing. Tenant details are partly masked.',
      setup: 'Buildings, rooms, prices and settings. Tenant details are partly masked, for example S••• 08x-xxx-5678.',
      tenants: 'Sees tenants’ full name, phone, email and address. Every time a tenant is opened, it is recorded.',
    },
    form: {
      instruction_label: 'What you want the FlowRent team to do',
      instruction_placeholder: 'For example: import the rooms of Building A from the file sent on LINE',
      instruction_required: 'Write what you want the FlowRent team to do',
      scope_label: 'Scope',
      buildings_label: 'Buildings',
      all_buildings: 'All buildings',
      some_buildings: 'Only these buildings',
      buildings_required: 'Choose at least one building',
      no_buildings: 'There are no buildings in this account yet',
      duration_label: 'For',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      submit: 'Allow',
      approve_hint: 'Choose the scope and the time below, then press Allow, or decline the request.',
    },
    grant: {
      active_prefix: 'Allowed',
      signup_prefix: 'Allowed at sign-up',
      expires: 'expires {{date}}',
      buildings_count_one: '{{count}} building',
      buildings_count_other: '{{count}} buildings',
      instruction: 'What you asked for: “{{text}}”',
      revoke: 'Withdraw permission',
      revoke_confirm_title: 'Withdraw permission?',
      revoke_confirm_body: 'The FlowRent team can no longer work in this account until you allow it again.',
      revoke_confirm_yes: 'Withdraw',
      revoked: 'Permission withdrawn',
      created: 'The FlowRent team may now help in your account',
    },
    request: {
      line: 'Request from the FlowRent team ({{name}}): “{{reason}}” · suggested scope: {{scope}}',
      expires_in_one: 'Expires in {{count}} hour',
      expires_in_other: 'Expires in {{count}} hours',
      decline: 'Decline',
      declined: 'Request declined',
      deep_link_gone: 'The request in your link has expired or has already been answered.',
    },
    history: {
      title: 'History',
      empty: 'The FlowRent team has not worked in this account yet.',
      load_failed: 'Could not load the history.',
      started: 'The FlowRent team ({{name}}) started a session',
      ended_one: 'The FlowRent team ({{name}}) ended the session ({{count}} item)',
      ended_other: 'The FlowRent team ({{name}}) ended the session ({{count}} items)',
      in_progress: 'In progress',
      show_details: 'Show details',
      hide_details: 'Hide details',
      details_failed: 'Could not load the details of this session.',
      no_changes: 'Nothing was changed in this session.',
      event_read: 'The FlowRent team ({{name}}) viewed',
      event_write: 'The FlowRent team ({{name}}) changed',
      event_subjects_one: '{{count}} tenant',
      event_subjects_other: '{{count}} tenants',
      more_events: 'Showing {{shown}} of {{total}} entries',
      case_reason: 'Case: {{reason}}',
      case_ticket: 'Reference {{ref}}',
    },
    summary: {
      create: '{{resource}} created: {{count}}',
      import: '{{resource}} imported: {{count}}',
      update: '{{resource}} updated: {{count}}',
      destroy: '{{resource}} deleted: {{count}}',
      resources: {
        account: 'Account settings',
        property: 'Buildings',
        room_type: 'Room types',
        unit: 'Rooms',
        meter: 'Meters',
        reading: 'Meter readings',
        customer: 'Tenants',
        lease: 'Leases',
        invoice: 'Invoices',
        invoice_item: 'Invoice lines',
        payment: 'Payments',
        expense: 'Expenses',
        vendor: 'Vendors',
        equipment: 'Equipment',
        maintenance_request: 'Repair requests',
        maintenance_schedule: 'Maintenance schedules',
        work_order: 'Work orders',
        parcel: 'Parcels',
        vehicle: 'Vehicles',
        document_template: 'Document templates',
        gl_account: 'Chart of accounts entries',
        journal_entry: 'Journal entries',
        fixed_asset: 'Assets',
        parking_zone: 'Parking zones',
        user: 'Users',
        file: 'Files',
        other: 'Other records',
      },
    },
    errors: {
      owner_email_recently_changed: 'The owner email was changed in the last 72 hours. Try again after that.',
      grant_active: 'The FlowRent team already has permission. Withdraw it first to give a new one.',
      request_expired: 'This request has expired. The FlowRent team can send a new one.',
      request_not_pending: 'This request has already been answered.',
      invalid_property_ids: 'One of the chosen buildings is no longer in this account.',
      instruction_required: 'Write what you want the FlowRent team to do',
      instruction_too_long: 'Keep it to 1,000 characters or fewer.',
      grant_not_found: 'There is no permission to withdraw.',
    },
  },

  // EP-160 T160-104w: Settings -> Personal data requests (owner and admin)
  data_requests: {
    tab_label: 'Personal data requests',
    title: 'Personal data requests',
    description: "Tenants' requests under their data-subject rights. Each must be answered within 30 days of the request.",
    empty: 'No requests yet',
    load_failed: 'Could not load the requests',
    unknown_customer: 'Unknown name',
    columns: {
      customer: 'Tenant',
      type: 'Type',
      requested_at: 'Requested',
      due_at: 'Due',
      status: 'Status',
      note: 'Message',
      actions: 'Actions',
    },
    types: {
      access: 'Copy of data',
      erasure: 'Erase data',
      rectification: 'Correct data',
      restriction: 'Restrict use',
      objection: 'Objection',
      withdrawal: 'Withdraw consent',
    },
    statuses: {
      pending: 'Awaiting approval',
      processing: 'In progress',
      completed: 'Approved',
      rejected: 'Rejected',
      link_sent: 'Link sent',
    },
    decision_note: 'Reason: {{note}}',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Request approved',
    approved_access: 'Approved. The tenant will be sent a download link.',
    rejected: 'Request rejected',
    erasure_confirm_title: 'Approve the erasure?',
    erasure_confirm_body: "{{name}}'s personal data will be erased and this cannot be undone. Invoices and payments are kept as the law requires, without the tenant's name.",
    erasure_confirm_yes: 'Erase data',
    reject_title: 'Reject the request',
    reject_body: "Give the reason for rejecting {{name}}'s request. The reason is kept with the request.",
    reject_reason_label: 'Reason',
    reject_reason_required: 'Enter a reason',
    reject_confirm: 'Reject request',
    lift: 'Lift restriction',
    lift_title: 'Lift the restriction',
    lift_body: "Give the reason for lifting the restriction on {{name}}'s data. The reason, and who lifted it, are kept with the request.",
    lift_reason_label: 'Reason',
    lift_reason_required: 'Enter a reason',
    lift_confirm: 'Lift restriction',
    lifted: 'Restriction lifted',
    lifted_still_restricted: 'Lifted. {{name}} stays restricted under another approved request.',
    lifted_note: 'Restriction lifted {{date}}: {{reason}}',
    errors: {
      request_already_decided: 'This request has already been decided',
      invalid_status: 'The status sent is not valid',
      note_too_long: 'The reason is longer than 1,000 characters',
      request_not_liftable: 'This restriction cannot be lifted (it is not approved or was already lifted)',
      lift_reason_required: 'Enter a reason',
      lift_reason_too_long: 'The reason is longer than 1,000 characters',
    },
  },

  // EP-160 T160-104w: Settings -> Delete account, the owner's "download all data"
  delete_account: {
    export_title: 'Download all data',
    export_body: "Get a copy of all the account's data before deleting it. The download link is sent to the account owner's email only.",
    export_button: 'Download all data',
    export_requested: "We will send the link to the account owner's email",
    export_owner_email_recently_changed: "The account owner's email changed in the last 72 hours. Ask again once that period has passed.",
    export_in_progress: "An earlier export is still being prepared. We will send its link to the account owner's email when it is done.",
  },

  // Actions
  actions: {
    save_settings: 'Save Settings',
  },

  // Messages
  messages: {
    invite_sent: 'Invitation sent successfully',
    invite_resent: 'Invitation resent successfully',
    role_updated: 'Role updated successfully',
    member_removed: 'Member removed successfully',
    remove_confirm: 'Remove {{name}} from the team?',
    cannot_remove_self: 'You cannot remove yourself from the team',
    settings_saved: 'Settings saved successfully',
    // PAY-1 (T149-082/083/084): shown instead of a success toast when a
    // save did not actually happen: a client validation stop or a server
    // failure. See SettingsSaveAlert.
    not_saved: 'Not saved',
    not_saved_field_count_one: 'Not saved: {{count}} field needs attention',
    not_saved_field_count_other: 'Not saved: {{count}} fields need attention',
  },

  // EP-156 T156-007 / EP-157 T157-005 — translated by translateApiError via
  // useApiErrorTranslator('settings').
  errors: {
    line_not_configured: 'Save the channel secret and access token first.',
    line_token_invalid: 'LINE rejected the saved channel access token.',
    webhook_not_flowrent: 'The webhook endpoint registered with LINE is not this FlowRent deployment.',
    webhook_signature_failed: 'LINE’s webhook test failed. The saved channel secret may be wrong.',
    app_id_required: 'Enter an iOS Bundle ID or an Android Package Name (at least one).',
    app_id_taken: 'This app is already registered to another account.',
    app_id_reserved: 'This id belongs to one of FlowRent’s own apps and cannot be used.',
    service_account_invalid: 'This file is not a valid Firebase service account key.',
    service_account_project_mismatch: 'This key belongs to a different Firebase project than the one entered.',
    encryption_not_configured: 'This environment is not yet set up to store keys.',
    app_integration_key_invalid: 'Firebase rejected the saved service account key.',
    app_integration_fcm_unreachable: 'Could not reach Firebase Cloud Messaging for this project.',
  },
}
